import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Mail, Globe, ArrowRight, Loader2, AlertTriangle, CheckCircle2, MapPin, Linkedin, Clock } from "lucide-react";
import { Card } from "../components/ui/card";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { submitContact } from "../lib/api";

const SUBJECTS = ["General Inquiry", "Audit Request", "Partnership", "Press", "Other"];

const FAQS = [
    { q: "What's the typical project timeline?", a: "Audits run 1–3 weeks depending on site size. Document remediation can be as fast as 48 hours per file. Ongoing engagements span quarters or years." },
    { q: "Do you work with agencies on white-label projects?", a: "Yes — we partner with web agencies, design studios, and IT consultancies as their on-call accessibility team. Co-branded deliverables available on request." },
    { q: "What if I'm not sure which standards apply to me?", a: "That's a perfect first conversation. Tell us your markets and product type — we'll outline the standards that matter and the ones that don't." },
    { q: "Do you offer ongoing support or one-time audits only?", a: "Both. Many clients start with a single audit and move to a quarterly retainer for monitoring, regression testing, and pre-release reviews." },
    { q: "How do you handle confidentiality and NDAs?", a: "We sign NDAs as standard practice. All client data is treated as confidential and stored under access controls aligned with our internal information-security policy." },
    { q: "What's your pricing model?", a: "Project-based for audits and remediation; monthly retainers for ongoing support. We provide written quotes after a short scoping call." },
];

const initial = { name: "", email: "", company: "", subject: "", message: "", consent: false, website: "" };

const validate = (v) => {
    const e = {};
    if (!v.name.trim()) e.name = "Please enter your name.";
    if (!v.email.trim()) e.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Please enter a valid email.";
    if (!v.subject) e.subject = "Choose a subject.";
    if (!v.message.trim()) e.message = "Please write a short message.";
    else if (v.message.length > 1000) e.message = "Please keep it under 1000 characters.";
    if (!v.consent) e.consent = "Consent is required.";
    return e;
};

const inputCls =
    "w-full min-h-[48px] px-4 rounded-md border border-brand-border bg-white text-brand-text placeholder:text-brand-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:border-brand-tealBright transition-colors";

const Field = ({ label, htmlFor, required, error, children, hint }) => (
    <div>
        <label htmlFor={htmlFor} className="block text-sm font-semibold text-brand-text mb-1.5">
            {label} {required && <span className="text-brand-error" aria-hidden="true">*</span>}
            {required && <span className="sr-only">(required)</span>}
        </label>
        {children}
        {hint && !error && <p className="mt-1 text-xs text-brand-muted">{hint}</p>}
        {error && (
            <p role="alert" id={`${htmlFor}-error`}
               className="mt-1 text-xs font-medium text-brand-error flex items-center gap-1">
                <AlertTriangle size={12} aria-hidden="true" /> {error}
            </p>
        )}
    </div>
);

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="contact-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Contact</p>
            <h1 id="contact-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Let's Talk Accessibility.
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                Whether you need an audit, have a compliance question, or want to partner — we're here.
            </p>
        </div>
    </section>
);

/* ---------------- 3 OPTION CARDS ---------------- */
const OptionsRow = () => {
    const cards = [
        {
            icon: Calendar,
            title: "Book a Discovery Call",
            desc: "15-minute call to understand your needs.",
            cta: "Schedule a call",
            href: "mailto:hello@accessnova.co?subject=Discovery%20call%20request",
            testId: "contact-card-call",
        },
        {
            icon: Mail,
            title: "Send a Message",
            desc: "Use the form below — we reply within 24 hours on weekdays.",
            cta: "Jump to form",
            href: "#contact-form",
            testId: "contact-card-message",
        },
        {
            icon: Globe,
            title: "Direct Channels",
            desc: null,
            extra: (
                <ul className="mt-4 space-y-2.5 text-sm text-brand-body">
                    <li className="flex items-center gap-2">
                        <Mail size={16} className="text-brand-teal flex-none" aria-hidden="true" />
                        <a href="mailto:hello@accessnova.co" className="text-brand-text hover:text-brand-teal underline-offset-2 hover:underline" data-testid="contact-direct-email">
                            hello@accessnova.co
                        </a>
                    </li>
                    <li className="flex items-center gap-2">
                        <Linkedin size={16} className="text-brand-teal flex-none" aria-hidden="true" />
                        <a href="https://linkedin.com/company/accessnova" target="_blank" rel="noopener noreferrer"
                           className="text-brand-text hover:text-brand-teal underline-offset-2 hover:underline" data-testid="contact-direct-linkedin">
                            linkedin.com/company/accessnova
                        </a>
                    </li>
                    <li className="flex items-center gap-2">
                        <MapPin size={16} className="text-brand-teal flex-none" aria-hidden="true" />
                        <span className="text-brand-body">India · Remote-first, globally distributed</span>
                    </li>
                    <li className="flex items-center gap-2">
                        <Clock size={16} className="text-brand-teal flex-none" aria-hidden="true" />
                        <span className="text-brand-body">Response: within 24 hours, weekdays</span>
                    </li>
                </ul>
            ),
            testId: "contact-card-direct",
        },
    ];
    return (
        <section aria-labelledby="contact-options" className="py-16 lg:py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <h2 id="contact-options" className="sr-only">Ways to reach us</h2>
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {cards.map((c) => {
                        const Icon = c.icon;
                        return (
                            <li key={c.title}>
                                <Card className="p-7 h-full border-brand-border bg-white hover:shadow-cardHover transition-shadow" data-testid={c.testId}>
                                    <div className="w-11 h-11 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center">
                                        <Icon size={22} aria-hidden="true" />
                                    </div>
                                    <h3 className="mt-5 text-lg font-bold text-brand-text">{c.title}</h3>
                                    {c.desc && <p className="mt-2 text-sm text-brand-body leading-relaxed">{c.desc}</p>}
                                    {c.extra}
                                    {c.cta && (
                                        <a href={c.href}
                                           {...(c.href.startsWith("mailto:") ? {} : {})}
                                           className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal hover:text-brand-tealBright">
                                            {c.cta}
                                            <ArrowRight size={14} aria-hidden="true" />
                                        </a>
                                    )}
                                </Card>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

/* ---------------- FORM ---------------- */
const ContactForm = () => {
    const [v, setV] = useState(initial);
    const [errors, setErrors] = useState({});
    const [state, setState] = useState("idle");
    const [serverErr, setServerErr] = useState("");

    const set = (k) => (e) =>
        setV((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const isValid = Object.keys(validate(v)).length === 0;

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(v);
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            document.getElementById(Object.keys(errs)[0])?.focus();
            return;
        }
        setState("loading");
        setServerErr("");
        try {
            await submitContact(v);
            setState("success");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setServerErr(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
            setState("idle");
        }
    };

    if (state === "success") {
        return (
            <section id="contact-form" aria-labelledby="cf-success" className="bg-brand-soft border-y border-brand-border py-20">
                <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D1FAE5] text-[#065F46] mb-6" aria-hidden="true">
                        <CheckCircle2 size={32} />
                    </div>
                    <h2 id="cf-success" className="text-3xl sm:text-4xl font-bold text-brand-text">Message sent.</h2>
                    <p className="mt-4 text-base text-brand-body">
                        We'll get back to you within 24 hours on weekdays. For anything urgent, email <strong>hello@accessnova.co</strong> directly.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section id="contact-form" aria-labelledby="cf-heading" className="bg-brand-soft border-y border-brand-border py-16 lg:py-20">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 id="cf-heading" className="text-3xl sm:text-4xl font-bold text-brand-text">Send a message</h2>
                <p className="mt-3 text-base text-brand-body">
                    Required fields are marked <span className="text-brand-error" aria-hidden="true">*</span>.
                </p>

                {serverErr && (
                    <div role="alert" aria-live="assertive"
                         className="mt-6 rounded-md border border-[#FECACA] bg-[#FEF2F2] text-[#991B1B] p-4 text-sm font-medium"
                         data-testid="contact-server-error">
                        {serverErr}
                    </div>
                )}

                <form onSubmit={onSubmit} noValidate className="mt-8 grid grid-cols-1 gap-5" data-testid="contact-form">
                    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                        <label>Website (do not fill)
                            <input type="text" tabIndex="-1" autoComplete="off" value={v.website} onChange={set("website")} />
                        </label>
                    </div>

                    <Field label="Full Name" htmlFor="name" required error={errors.name}>
                        <input id="name" type="text" autoComplete="name" required value={v.name} onChange={set("name")}
                               className={inputCls} aria-invalid={!!errors.name}
                               aria-describedby={errors.name ? "name-error" : undefined}
                               data-testid="contact-input-name" />
                    </Field>
                    <Field label="Email" htmlFor="email" required error={errors.email}>
                        <input id="email" type="email" autoComplete="email" required value={v.email} onChange={set("email")}
                               className={inputCls} aria-invalid={!!errors.email}
                               aria-describedby={errors.email ? "email-error" : undefined}
                               data-testid="contact-input-email" />
                    </Field>
                    <Field label="Company" htmlFor="company">
                        <input id="company" type="text" autoComplete="organization" value={v.company} onChange={set("company")}
                               className={inputCls} data-testid="contact-input-company" />
                    </Field>
                    <Field label="Subject" htmlFor="subject" required error={errors.subject}>
                        <select id="subject" required value={v.subject} onChange={set("subject")} className={inputCls}
                                aria-invalid={!!errors.subject}
                                aria-describedby={errors.subject ? "subject-error" : undefined}
                                data-testid="contact-input-subject">
                            <option value="">Select a subject…</option>
                            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Message" htmlFor="message" required error={errors.message}
                           hint={`${v.message.length}/1000 characters`}>
                        <textarea id="message" rows={5} maxLength={1000} required value={v.message} onChange={set("message")}
                                  className={`${inputCls} min-h-[140px] py-3 resize-y`}
                                  aria-invalid={!!errors.message}
                                  aria-describedby={errors.message ? "message-error" : undefined}
                                  data-testid="contact-input-message" />
                    </Field>
                    <Field label="" htmlFor="consent" error={errors.consent}>
                        <label className="flex items-start gap-3 text-sm text-brand-body cursor-pointer">
                            <input id="consent" type="checkbox" required checked={v.consent} onChange={set("consent")}
                                   className="mt-1 w-4 h-4 accent-[#127A75]"
                                   aria-invalid={!!errors.consent}
                                   aria-describedby={errors.consent ? "consent-error" : undefined}
                                   data-testid="contact-input-consent" />
                            <span>
                                I agree to be contacted regarding my message. We respect privacy and never share data.
                                <span className="text-brand-error" aria-hidden="true"> *</span>
                            </span>
                        </label>
                    </Field>

                    <button type="submit" disabled={!isValid || state === "loading"}
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="contact-submit">
                        {state === "loading" ? (
                            <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Submitting…</>
                        ) : (
                            <>Submit Message <ArrowRight size={18} aria-hidden="true" /></>
                        )}
                    </button>
                </form>
            </div>
        </section>
    );
};

/* ---------------- FAQ ---------------- */
const FAQ = () => (
    <section aria-labelledby="contact-faq" className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Common questions</p>
            <h2 id="contact-faq" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">Before you reach out.</h2>
            <Accordion type="single" collapsible className="mt-8 bg-white rounded-lg border border-brand-border" data-testid="contact-faq-accordion">
                {FAQS.map((f, i) => (
                    <AccordionItem key={f.q} value={`q${i}`} className="border-b last:border-b-0 border-brand-border px-5">
                        <AccordionTrigger className="text-left font-semibold text-brand-text hover:no-underline py-5">
                            {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-brand-body leading-relaxed pb-5">{f.a}</AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    </section>
);

/* ---------------- PAGE ---------------- */
export default function Contact() {
    useEffect(() => { document.title = "Contact — AccessNova"; }, []);
    return (
        <>
            <Hero />
            <OptionsRow />
            <ContactForm />
            <FAQ />
        </>
    );
}
